import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk, RootState } from "./store";
import { AuthService } from "../services/AuthService";
import { setUserInfo, setRegisterStatus } from "./UserSlice";
import { Api } from "../services/Api";
import { PriceApiResponse } from "../models/PriceApi";
import { saveSession, clearSession } from "../utils/session";

export const TOKEN_KEY = 'token';

interface AuthState {
  loggedStatus: boolean,
  type: string | null,
  organization: string,
  organizationId: number | null,
  priceBearer: PriceApiResponse,
  price: any,
  authLoading: boolean,
  loadingPrices: boolean,
  pricesError: string | null,
  secretKey: 'GengarIsTheBest'
}

// users type
// admin, user, compliance, finance

const initialState: AuthState = {
  loggedStatus: false,
  type: null,
  organization: '',
  organizationId: null,
  priceBearer: {
    token: '',
    expiresAtMs: ''
  },
  price: null,
  authLoading: false,
  loadingPrices: true,
  pricesError: null,
  secretKey: 'GengarIsTheBest'
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setLoggedStatus: (state, action: PayloadAction<boolean>) => {
            state.loggedStatus = action.payload;
        },
        setLoggedType: (state, action: PayloadAction<string>) => {
            state.type = action.payload;
        },
        setLoggedOrganization: (state, action: PayloadAction<string>) => {
            state.organization = action.payload;
        },
        setLoggedOrganizationId: (state, action: PayloadAction<number>) => {
            state.organizationId = action.payload;
        },
        setPriceBearer: (state, action: PayloadAction<PriceApiResponse>) => {
            state.priceBearer = action.payload;
        },
        setUsdPrice: (state, action: PayloadAction<any>) => {
            state.price = action.payload;
        },
        setAuthLoading: (state, action: PayloadAction<boolean>) => {
            state.authLoading = action.payload;
        },
        setLoadingPrices: (state, action: PayloadAction<boolean>) => {
            state.loadingPrices = action.payload;
        },
        setPricesError: (state, action: PayloadAction<string | null>) => {
            state.pricesError = action.payload;
        },
    }
  });
  
export const {
  setLoggedStatus, 
  setLoggedType, 
  setLoggedOrganization, 
  setLoggedOrganizationId,
  setPriceBearer,
  setUsdPrice,
  setAuthLoading,
  setLoadingPrices,
  setPricesError
} = authSlice.actions;

// customs func
export const getLoggedUser = (state: RootState): boolean | null => state.auth.loggedStatus;
export const getLoggedUserType = (state: RootState): string | null => state.auth.type;
export const getLoggedOrganization = (state: RootState): string  => state.auth.organization;

export const makeUserLogin = (nickname:string, password:string) : AppThunk<Promise<boolean>> => async (dispatch, getState) => {

  try {
    const loginResponse = await AuthService.makeUserLogin(nickname,password);
    saveSession(loginResponse);

    Api.setUserInfo(loginResponse);
    dispatch(setLoggedStatus(true));
    dispatch(setLoggedType(loginResponse.role));
    dispatch(setUserInfo(loginResponse))
    dispatch(setAuthLoading(false));
    return true;
  } catch (e) {
    console.dir(e);
    dispatch(setAuthLoading(false));
    return false;
  }

}

export const makeUserRegister = (name:string, email:string, password:string) : AppThunk<Promise<{ ok: boolean; error?: string }>> => async (dispatch) => {

  dispatch(setRegisterStatus(true));
  try {
    const registerResponse = await AuthService.registerUser(name, email, password);
    saveSession(registerResponse);

    Api.setUserInfo(registerResponse);
    dispatch(setLoggedStatus(true));
    dispatch(setLoggedType(registerResponse.role));
    dispatch(setUserInfo(registerResponse));
    return { ok: true };
  } catch (e: any) {
    console.dir(e);
    const error = e?.response?.data?.message ?? 'Nao foi possivel concluir o registro.';
    return { ok: false, error: Array.isArray(error) ? error.join(', ') : error };
  } finally {
    dispatch(setRegisterStatus(false));
  }

}

export const makeCompanyRegister =
  (companyName: string, owner: { name: string; email: string; password: string }, cnpj?: string)
  : AppThunk<Promise<{ ok: boolean; error?: string }>> => async (dispatch) => {

  dispatch(setRegisterStatus(true));
  try {
    const registerResponse = await AuthService.registerCompany(companyName, owner, cnpj);
    saveSession(registerResponse);

    Api.setUserInfo(registerResponse);
    dispatch(setLoggedStatus(true));
    dispatch(setLoggedType(registerResponse.role));
    dispatch(setUserInfo(registerResponse));
    return { ok: true };
  } catch (e: any) {
    console.dir(e);
    const error = e?.response?.data?.message ?? 'Nao foi possivel concluir o registro da empresa.';
    return { ok: false, error: Array.isArray(error) ? error.join(', ') : error };
  } finally {
    dispatch(setRegisterStatus(false));
  }

}

export const verify = () : AppThunk<Promise<void>> => async (dispatch) => {
  const valid = await AuthService.verifyToken();
  if (!valid) console.warn('verify: token nao confirmado (seguindo com a sessao local)');
}

const PRICE_TOKEN_SKEW_MS = 30_000;

let priceRequestInFlight: Promise<void> | null = null;

const isPriceTokenUsable = (bearer: PriceApiResponse): boolean => {
  if (!bearer?.token) return false;

  const expiresAt = Number(bearer.expiresAtMs);
  if (!Number.isFinite(expiresAt) || expiresAt <= 0) return true;

  return Date.now() < expiresAt - PRICE_TOKEN_SKEW_MS;
};

const renewPriceToken = () : AppThunk<Promise<string>> => async (dispatch) => {
  try {
    const loginResponse : PriceApiResponse = await AuthService.makePriceApiLogin();
    if (!loginResponse?.token) throw new Error('login da API de precos veio sem token');

    dispatch(setPriceBearer(loginResponse));
    return loginResponse.token;
  } catch (e) {
    console.error('renewPriceToken:', e);
    return '';
  }
}

export const loginPriceApi = () : AppThunk<Promise<void>> => async (dispatch) => {
  const token = await dispatch(renewPriceToken());
  if (token) await dispatch(fetchTokenPrice());
}

export const fetchTokenPrice = () : AppThunk<Promise<void>> => async (dispatch, getState) => {

  if (priceRequestInFlight) return priceRequestInFlight;

  priceRequestInFlight = (async () => {
    dispatch(setLoadingPrices(true));

    try {

      let token = getState().auth.priceBearer.token;
      if (!isPriceTokenUsable(getState().auth.priceBearer)) {
        token = await dispatch(renewPriceToken());
      }
      if (!token) throw new Error('sem token da API de precos');

      try {
        dispatch(setUsdPrice(await AuthService.getPrice(token)));
      } catch (e) {
        const renewed = await dispatch(renewPriceToken());
        if (!renewed) throw e;
        dispatch(setUsdPrice(await AuthService.getPrice(renewed)));
      }

      dispatch(setPricesError(null));
    } catch (e) {
      console.error('fetchTokenPrice:', e);
      dispatch(setPricesError('Cotacao desatualizada — falha ao consultar a API de precos'));
    } finally {
      dispatch(setLoadingPrices(false));
      priceRequestInFlight = null;
    }
  })();

  return priceRequestInFlight;
}

export const AuthLogout = () : AppThunk<Promise<void>> => async (dispatch, getState) => {
    clearSession();
    dispatch(setLoggedStatus(false));
    dispatch(setLoggedType(''));
    dispatch(setAuthLoading(false));
}

export default authSlice.reducer;